// components/ExpenseDataGrid.js
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CategoryIcon from '@mui/icons-material/Category';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WarningIcon from '@mui/icons-material/Warning';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableRowsIcon from '@mui/icons-material/TableRows';

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontSize: '1rem',
        fontWeight: 600,
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiDataGrid-row:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: theme.palette.action.selected,
    },
    '& .MuiDataGrid-cell': {
        fontSize: '0.9rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnSeparator': {
        display: 'none',
    },
    '& .MuiDataGrid-footerContainer': {
        backgroundColor: theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    '& .MuiTablePagination-root': {
        color: theme.palette.text.secondary,
    },
    '& .MuiDataGrid-toolbarContainer': {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    // Customize cell focusing to be more subtle
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
        padding: '0 16px',
    },
    // Add custom border radius to the top corners
    borderRadius: theme.shape.borderRadius,
    border: 'none',
    overflow: 'hidden',
    boxShadow: theme.shadows[1],
}));

const NoRowsOverlay = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(6),
    '& .MuiSvgIcon-root': {
        fontSize: '4rem',
        marginBottom: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
}));

const CustomNoRowsOverlay = () => (
    <NoRowsOverlay>
        <SearchOffIcon />
        <Typography variant="h6" color="text.secondary" gutterBottom>
            No Expenses Found
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filters to find what you're looking for
        </Typography>
    </NoRowsOverlay>
);

const DataGridWrapper = styled(Paper)(({ theme }) => ({
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    position: 'relative',
}));

const GridHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
}));

const ExpenseDataGrid = ({
                             expenses,
                             expenseTypes,
                             properties,
                             onViewDetails,
                             onDeleteExpense,
                             getExpenseTypeName,
                             getPropertyName
                         }) => {
    const [pageSize, setPageSize] = useState(10);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [expenseToDelete, setExpenseToDelete] = useState(null);

    const handleDeleteClick = (expense) => {
        setExpenseToDelete(expense);
        setConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        if (expenseToDelete) {
            onDeleteExpense(expenseToDelete.id);
            setConfirmDelete(false);
            setExpenseToDelete(null);
        }
    };

    const handleCloseDialog = () => {
        setConfirmDelete(false);
        setExpenseToDelete(null);
    };

    const columns = [
        {
            field: 'description',
            headerName: 'Description',
            minWidth: 300,
            flex: 1,
            renderCell: (params) => (
                <Tooltip
                    title={params.value}
                    enterDelay={500}
                    TransitionComponent={Fade}
                    TransitionProps={{ timeout: 600 }}
                >
                    <Typography variant="body2" sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                    }}>
                        {params.value}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'house_number',
            headerName: 'HSE NO',
            headerAlign: 'center',
            align: 'center',
            minWidth: 120,
            flex: 0.7,
            renderCell: (params) => (
                params.value && params.value !== '' ? (
                    <Chip
                        label={params.value}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                            borderRadius: '6px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: '28px'
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                )
            ),
        },
        {
            field: 'expense_type',
            headerName: 'Expense Type',
            // headerAlign: 'center',
            // align: 'center',
            minWidth: 160,
            flex: 0.9,
            renderCell: (params) => {
                const typeName = getExpenseTypeName(params.value);
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', gap: 1 }}>
                        {/*<CategoryIcon fontSize="small" color="primary" />*/}
                        <Typography variant="body2">{typeName}</Typography>
                    </Box>
                );
            },
        },
        {
            field: 'property_id',
            headerName: 'Property',
            // headerAlign: 'center',
            // align: 'center',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => {
                const propertyName = getPropertyName(params.value);
                return (
                    <Tooltip
                        title={propertyName}
                        enterDelay={500}
                        TransitionComponent={Fade}
                        TransitionProps={{ timeout: 600 }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, maxWidth: '100%' }}>
                            <HomeWorkIcon fontSize="small" sx={{ color: '#f06292', flexShrink: 0 }} />
                            <Typography noWrap variant="body2">{propertyName}</Typography>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'cost',
            headerName: 'Amount',
            headerAlign: 'center',
            align: 'center',
            minWidth: 150,
            flex: 0.8,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="600" color="success.main">
                    {parseFloat(params.value).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'KSH',
                    })}
                </Typography>
            ),
        },
        {
            field: 'date_created',
            headerName: 'Date',
            headerAlign: 'center',
            align: 'center',
            minWidth: 160,
            flex: 0.9,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonthIcon fontSize="small" color="action" />
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerAlign: 'center',
            align: 'center',
            minWidth: 160,
            flex: 0.8,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                        <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={<InfoIcon />}
                            onClick={() => onViewDetails(params.row)}
                            sx={{
                                borderRadius: '6px',
                                textTransform: 'none',
                                padding: '4px 12px'
                            }}
                        >
                            Details
                        </Button>
                    </Tooltip>

                    <Tooltip title="Delete Expense">
                        <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(params.row)}
                            sx={{
                                border: '1px solid',
                                borderColor: 'error.light',
                                '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'error.contrastText'
                                }
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <>
            <DataGridWrapper>
                <GridHeader>
                    <TableRowsIcon color="primary" />
                    <Typography variant="h6" fontWeight="500">
                        Expense Records
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({expenses.length} items)
                    </Typography>
                </GridHeader>

                <Box sx={{ height: 500, width: '100%' }}>
                    <StyledDataGrid
                        rows={expenses}
                        columns={columns}
                        pageSize={pageSize}
                        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                        rowsPerPageOptions={[5, 10, 15, 20, 25]}
                        disableSelectionOnClick
                        components={{
                            NoRowsOverlay: CustomNoRowsOverlay,
                            Toolbar: GridToolbar,
                        }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 300 },
                                printOptions: { disableToolbarButton: false },
                                csvOptions: { disableToolbarButton: false },
                            },
                        }}
                        initialState={{
                            sorting: {
                                sortModel: [{ field: 'date_created', sort: 'desc' }],
                            },
                        }}
                        sx={{ border: 'none' }}
                    />
                </Box>
            </DataGridWrapper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDelete}
                onClose={handleCloseDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    elevation: 3,
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle id="alert-dialog-title" sx={{
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <WarningIcon />
                    Delete Expense
                </DialogTitle>
                <DialogContent sx={{ pt: 2, pb: 1, px: 3, mt: 1 }}>
                    <DialogContentText id="alert-dialog-description">
                        {expenseToDelete && (
                            <>
                                <Typography paragraph>
                                    Are you sure you want to delete this expense? This action cannot be undone.
                                </Typography>
                                <Box sx={{
                                    backgroundColor: 'grey.100',
                                    p: 2,
                                    borderRadius: 1,
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Description:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="500" gutterBottom>
                                        {expenseToDelete.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Amount:
                                            </Typography>
                                            <Typography variant="body1" fontWeight="500" color="error.main">
                                                {parseFloat(expenseToDelete.cost).toLocaleString('en-US', {
                                                    style: 'currency',
                                                    currency: 'KSH',
                                                })}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Date:
                                            </Typography>
                                            <Typography variant="body1">
                                                {expenseToDelete.date_created}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseDialog}
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        sx={{ borderRadius: '8px' }}
                        startIcon={<DeleteIcon />}
                    >
                        Delete Expense
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

ExpenseDataGrid.propTypes = {
    expenses: PropTypes.array.isRequired,
    expenseTypes: PropTypes.array.isRequired,
    properties: PropTypes.array.isRequired,
    onViewDetails: PropTypes.func.isRequired,
    onDeleteExpense: PropTypes.func.isRequired,
    getExpenseTypeName: PropTypes.func.isRequired,
    getPropertyName: PropTypes.func.isRequired,
};

export default ExpenseDataGrid;